import React, { useState, useEffect } from "react";
import axios from "axios";
import UpdateCommunity from "./UpdateCommunity";
import { config } from "../../../../../config";

const apiUrl = config.apiUrl;

const ViewCommunity = ({ onCommunityUpdated }) => {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    title: "",
    subtitle: "",
    facebook_group_link: "",
    icon: "",
    benefits: "",
    button_text: "",
    button_icon: ""
  });

  const fetchCommunities = async () => {
    try {
      const response = await axios.get(`${apiUrl}/communities`);
      setCommunities(response.data.data || response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load Communities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommunities();
  }, []);

  const handleEdit = (community) => {
    setEditingCommunity(community.id);
    setFormData({
      slug: community.slug,
      title: community.title,
      subtitle: community.subtitle,
      facebook_group_link: community.facebook_group_link,
      icon: community.icon,
      benefits: community.benefits,
      button_text: community.button_text,
      button_icon: community.button_icon
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.put(`${apiUrl}/communitiesupdate/${editingCommunity}`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (onCommunityUpdated) {
        onCommunityUpdated();
      }
      await fetchCommunities();
      setEditingCommunity(null);
      alert("Community updated successfully!");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "An error occurred while updating the Community.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Community?")) return;
    
    try {
      await axios.delete(`${apiUrl}/communitydelete/${id}`);
      setCommunities(communities.filter(item => item.id !== id));
      alert("Community deleted successfully");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete Community");
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading Communities...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow rounded">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">All Communities</h2>
      </div>

      {communities.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No Communities found. Create your first Community.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facebook Group</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {communities.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                    <div className="text-sm text-gray-500">{item.subtitle}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <a href={item.facebook_group_link} className="text-blue-600 hover:underline text-sm" target="_blank" rel="noopener noreferrer">
                      Join Group
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingCommunity && (
        <div className="mt-8">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 max-h-screen overflow-y-auto">
            <div className="p-6 mx-auto bg-white shadow rounded-lg min-w-[800px]">
              <UpdateCommunity
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                loading={loading}
                error={error}
                onCancel={() => setEditingCommunity(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewCommunity;